import React, { useContext, useEffect } from "react";
import "./availability-filter-container.scss";
import { SizeSelectField } from "../../components/SizeSelectField";
import { Checkbox } from "../../components/Checkbox";
import { PostcodeContainer } from "../PostcodeContainer";
import { FilterContext } from "../../FilterContext";
import { AttributeFilter, AttributeFilterOption, CollectionDesignation } from "../../../common/interfaces";
import { RentalDateField } from "../RentalDayContainer";
import { attributeFilterOptionByValue, isGcSessionReady } from "../../../common/util";
import { AVAILABLE_NOW_SERVICE_EVENTS, DATE_PICKER_EVENTS } from "../../../common/constants";
import { setPostcode } from "../../sessionService";

export interface AvailabilityFilter {
  availableNow: boolean;
  postcode: string;
  deliveryDates: {
    startDate: string;
    endDate: string;
  };
}

function hasOneSizeAvailable(availableAttributeFilters: AttributeFilter[]) {
  const sizeFilter = availableAttributeFilters.find(f => f.code === "size");
  return sizeFilter && !!sizeFilter.options.find(o => o.value === "OneSize");
}

function sizeAttributeFilterOnlyHasOneSizeOption(availableAttributeFilters: AttributeFilter[]) {
  const sizeFilter = availableAttributeFilters.find(f => f.code === "size");
  return sizeFilter && sizeFilter.options.filter(o => o.value !== "OneSize").length === 0;
}

export const AvailabilityFilterContainer: React.FC = () => {
  // TODO: Instead of using refs to update state, these components should use FilterContext state
  let datesRef: RentalDateField | null;
  let postcodeRef: PostcodeContainer | null;
  let sizeSelectRef: SizeSelectField | null;

  const {
    availableAttributeFilters,
    appliedAttributeFilters,
    applyAttributeFilter,

    availabilityFilter,
    applyAvailabilityFilter,
    clearAvailabilityFilters,

    collectionFilter,
    availableOnDemandFilters,
    availableSubscriptionFilters,
    availableSaleFilters
  } = useContext(FilterContext);

  const getAppliedSizes = () => {
    const appliedSizeFilter = appliedAttributeFilters.find(f => f.code === "size");
    if (appliedSizeFilter) {
      return appliedSizeFilter.options.map(o => o.value);
    } else {
      return [];
    }
  };

  useEffect(() => {
    if (sizeSelectRef) {
      sizeSelectRef.setState({ appliedSizeFilters: new Set(getAppliedSizes()) });
    }
  }, [appliedAttributeFilters]);

  const handleAvailableNowToggle = () => {
    const newValue = !availabilityFilter.availableNow;
    applyAvailabilityFilter({ availableNow: newValue });
    if (isGcSessionReady()) {
      // @ts-ignore
      GlamCornerCore.bus.broadcast(AVAILABLE_NOW_SERVICE_EVENTS.SET_AVAILABLE_NOW, { availableNow: newValue });
    }
  };

  const clearFilters = () => {
    clearAvailabilityFilters();
    if (datesRef) {
      datesRef.setState({ dates: null });
    }
    if (postcodeRef) {
      postcodeRef.setState({ postcode: "" });
      const postcodeInput: HTMLInputElement | null = document.querySelector(".js-postcode-input");
      if (postcodeInput) {
        postcodeInput.value = "";
      }
    }
    if (sizeSelectRef) {
      sizeSelectRef.setState({ appliedSizeFilters: new Set() });
    }
  };

  const handleSelectedSizedChange = (sizes: string[]) => {
    const sizeOptions: AttributeFilterOption[] = sizes.map(s => attributeFilterOptionByValue(s));
    const newSizeAttributeFilter: AttributeFilter = {
      code: "size",
      label: "Size",
      options: []
    };
    applyAttributeFilter({ ...newSizeAttributeFilter, options: sizeOptions });
  };

  const handlePostcodeFilterChange = ({ postcode }: { postcode: string }) => {
    applyAvailabilityFilter({ postcode });
    setPostcode(postcode);
  };

  const handleDeliveryDateChange = (dates: { startDate: string; endDate: string } | null) => {
    const deliveryDates = {
      startDate: dates ? dates.startDate : "",
      endDate: dates ? dates.endDate : ""
    };
    applyAvailabilityFilter({ deliveryDates });
    if (isGcSessionReady()) {
      // @ts-ignore
      GlamCornerCore.bus.broadcast(DATE_PICKER_EVENTS.DATE_SELECTED, deliveryDates);
      // @ts-ignore
      GlamCornerCore.bus.broadcast(DATE_PICKER_EVENTS.DATE_CHANGED, deliveryDates);
    }
  };

  const filterSize = (size: any) => {
    if (hasOneSizeAvailable(availableAttributeFilters) && size.sizeId === "OneSize") {
      return true;
    }

    const availableSizesForSubscription =
      !!availableSubscriptionFilters && availableSubscriptionFilters.size
        ? Object.keys(availableSubscriptionFilters.size)
        : [];
    const availableSizesForOnDemand =
      !!availableOnDemandFilters && availableOnDemandFilters.size ? Object.keys(availableOnDemandFilters.size) : [];

    const availableSizesForSale =
      !!availableSaleFilters && availableSaleFilters.size ? Object.keys(availableSaleFilters.size) : [];
    const isOnDemand = collectionFilter.collectionDesignation === CollectionDesignation.OnDemand;
    const isSubscription = collectionFilter.collectionDesignation === CollectionDesignation.Subscription;
    const isAvailableToBuy = collectionFilter.collectionDesignation === CollectionDesignation.AvailableToBuy;

    if (isOnDemand && availableSizesForOnDemand.find(o => o === size.label)) {
      return true;
    }

    if (isSubscription && availableSizesForSubscription.find(o => o === size.label)) {
      return true;
    }

    if (isAvailableToBuy && availableSizesForSale.find(o => o === size.label)) {
      return true;
    }

    return false;
  };

  type Field = { label: string | null | JSX.Element; component: JSX.Element; code: string };
  const fields: { [key: string]: Field } = {
    size: {
      code: "size",
      label: "Sizes",
      component: (
        <SizeSelectField
          ref={df => {
            sizeSelectRef = df;
          }}
          filter={filterSize}
          onApply={handleSelectedSizedChange}
          initialAppliedSizes={getAppliedSizes()}
        />
      )
    },
    availableNow: {
      code: "available-now",
      label: null,
      component: (
        <Checkbox
          key="available-now"
          inputId={"available-now"}
          checked={availabilityFilter.availableNow}
          onChange={handleAvailableNowToggle}
          data-testid="available-now-checkbox"
          className="c-catalogue__available-now-checkbox"
        >
          Available Now
        </Checkbox>
      )
    },
    postcode: {
      code: "postcode",
      label: "Postcode",
      component: (
        <PostcodeContainer
          ref={df => (postcodeRef = df)}
          key="postcode"
          onChange={handlePostcodeFilterChange}
          value={availabilityFilter.postcode}
        />
      )
    },
    deliveryDates: {
      code: "delivery-dates",
      label: (
        <span>
          Delivery Date
          <div className="field-with-label__aux-action">
            <a onClick={clearFilters} className="js-clear-calendar-filter field-with-label__aux-action-link">
              Clear
            </a>
          </div>
        </span>
      ),
      component: (
        <RentalDateField
          ref={df => {
            datesRef = df;
          }}
          isDressAvailable={() => {
            return true;
          }}
          initialAppliedDates={availabilityFilter.deliveryDates}
          postcode={availabilityFilter.postcode}
          onDatePicked={handleDeliveryDateChange}
        />
      )
    }
  };

  const collectionAvailabilityFilters: { [key: string]: Field[] } = {
    [CollectionDesignation.All]: [],
    [CollectionDesignation.AvailableToBuy]: sizeAttributeFilterOnlyHasOneSizeOption(availableAttributeFilters)
      ? []
      : [fields.size],
    [CollectionDesignation.OnDemand]: [fields.postcode, fields.size, fields.deliveryDates],
    [CollectionDesignation.Subscription]: [fields.size, fields.availableNow]
  };

  if (collectionAvailabilityFilters[collectionFilter.collectionDesignation].length === 0) {
    return null;
  }

  return (
    <div className="c-availability-filter">
      <h3 className="c-availability-filter__heading">Check availability</h3>
      <div className="c-availability-filter__fields" data-collection={collectionFilter.collectionDesignation}>
        {collectionAvailabilityFilters[collectionFilter.collectionDesignation].map(
          ({ label, component, code }: Field, index: number) => (
            <div key={index} className={`c-availability-filter__field c-availability-filter__field--${code}`}>
              <h3 className="c-availability-filter__field-label">{label}</h3>
              {component}
            </div>
          )
        )}
      </div>
    </div>
  );
};
